import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
export const InteractionContext = createContext(null);
export function InteractionProvider({ children, initialScreen, initialOpenOverlays, onScreenChange, }) {
    const [state, setState] = useState({
        openOverlays: new Set(initialOpenOverlays),
        currentScreen: initialScreen || null,
    });
    // Track the previous initialScreen value to detect external changes
    const prevInitialScreenRef = useRef(initialScreen);
    // Sync currentScreen only when initialScreen prop actually changes (e.g., from external dropdown)
    // Not when currentScreen changes due to internal navigation
    useEffect(() => {
        if (initialScreen && initialScreen !== prevInitialScreenRef.current) {
            prevInitialScreenRef.current = initialScreen;
            setState((prev) => ({
                ...prev,
                currentScreen: initialScreen,
                openOverlays: new Set(), // Close overlays on external navigation
            }));
        }
    }, [initialScreen]);
    const openOverlay = useCallback((id) => {
        setState((prev) => {
            // Skip if already open
            if (prev.openOverlays.has(id))
                return prev;
            // Create new Set with added item
            const newOverlays = new Set(prev.openOverlays);
            newOverlays.add(id);
            return { ...prev, openOverlays: newOverlays };
        });
    }, []);
    const closeOverlay = useCallback((id) => {
        setState((prev) => {
            // Skip if not open
            if (!prev.openOverlays.has(id))
                return prev;
            // Create new Set without the item
            const newOverlays = new Set(prev.openOverlays);
            newOverlays.delete(id);
            return { ...prev, openOverlays: newOverlays };
        });
    }, []);
    const closeAllOverlays = useCallback(() => {
        setState((prev) => {
            // Skip if already empty
            if (prev.openOverlays.size === 0)
                return prev;
            return { ...prev, openOverlays: new Set() };
        });
    }, []);
    const isOverlayOpen = useCallback((id) => state.openOverlays.has(id), [state.openOverlays]);
    const navigateTo = useCallback((screenId) => {
        setState((prev) => ({
            ...prev,
            currentScreen: screenId,
            openOverlays: new Set(), // Close overlays on navigation
        }));
        onScreenChange?.(screenId);
    }, [onScreenChange]);
    const handleAction = useCallback((to) => {
        if (!to)
            return;
        // Handle action keywords
        if (to === ':close' || to === 'close') {
            // Close the most recently opened overlay
            const overlays = Array.from(state.openOverlays);
            if (overlays.length > 0) {
                closeOverlay(overlays[overlays.length - 1]);
            }
            return;
        }
        if (to === ':back' || to === 'back') {
            // Could implement history, for now just close overlays
            closeAllOverlays();
            return;
        }
        if (to === ':submit' || to === 'submit') {
            // Form submit - close overlays for now
            closeAllOverlays();
            return;
        }
        // Handle overlay references (start with #)
        if (to.startsWith('#')) {
            const overlayId = to.slice(1);
            openOverlay(overlayId);
            return;
        }
        // Handle screen navigation
        navigateTo(to);
    }, [state.openOverlays, openOverlay, closeOverlay, closeAllOverlays, navigateTo]);
    const value = {
        state,
        openOverlay,
        closeOverlay,
        closeAllOverlays,
        isOverlayOpen,
        navigateTo,
        handleAction,
        onScreenChange,
    };
    return _jsx(InteractionContext.Provider, { value: value, children: children });
}
export function useInteraction() {
    const context = useContext(InteractionContext);
    if (!context) {
        // Return a no-op context for non-interactive rendering
        return {
            state: { openOverlays: new Set(), currentScreen: null },
            openOverlay: () => { },
            closeOverlay: () => { },
            closeAllOverlays: () => { },
            isOverlayOpen: () => false,
            navigateTo: () => { },
            handleAction: () => { },
        };
    }
    return context;
}
//# sourceMappingURL=InteractionContext.js.map